/**
 * Field metadata for every tunable. Drives both the settings form and `store.ts` validation.
 *
 * Validation goes through THIS, not `typeof`. `typeof` cannot tell you that 'middle' is not a
 * valid `alignMode`, that `[300]` is not a valid latency range, or that `MACHINE.widthM = 0`
 * produces an invisible machine rather than an error.
 */

import { DEFAULTS } from './defaults';

export type FieldKind = 'number' | 'int' | 'boolean' | 'enum' | 'text' | 'multiline' | 'range2' | 'color';

export interface Field {
  /** Dot path into the config object, e.g. 'HERO_CAN.distanceM'. */
  readonly path: string;
  readonly group: string;
  readonly label: string;
  readonly kind: FieldKind;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly options?: readonly string[];
  readonly unit?: string;
  readonly help?: string;
  /** Build-time values: shown but not editable, because editing them changes nothing at runtime. */
  readonly readOnly?: boolean;
  /** Allow null in addition to the declared kind (used for "disabled" timings). */
  readonly nullable?: boolean;
}

const G = {
  target: 'Target',
  align: 'Alignment',
  hero: 'Hero can',
  tracking: 'Tracking',
  render: 'Rendering / Lighting',
  interaction: 'Interaction',
  timing: 'Timing',
  animation: 'Animation',
  prize: 'Prize',
  coupon: 'Coupon save',
  asset: 'Assets',
  debug: 'Debug',
  copy: 'Copy',
} as const;

/** Everything except COPY, which is generated below. */
const CORE_FIELDS: readonly Field[] = [
  // --- Target ---------------------------------------------------------------
  {
    path: 'TARGET.widthM', group: G.target, label: 'Target width', kind: 'number',
    min: 0.01, max: 20, step: 0.001, unit: 'm',
    help: 'Real width of the CROPPED region compiled into targets.mind. Every real-world dimension scales off this.',
  },
  { path: 'TARGET.depthM', group: G.target, label: 'Target depth', kind: 'number', min: 0, max: 5, step: 0.001, unit: 'm' },
  {
    path: 'TARGET.imagePxWidth', group: G.target, label: 'Compiled image width', kind: 'int',
    min: 64, max: 8192, step: 1, unit: 'px',
    help: 'DEFINES THE WORLD UNIT. Asserted against the loaded .mind at boot — a mismatch throws.',
  },
  {
    path: 'TARGET.imageAspect', group: G.target, label: 'Compiled image aspect', kind: 'number',
    min: 0.1, max: 10, step: 0.001, help: 'height / width. Emitted by `npm run asset:target`.',
  },

  // --- Alignment ------------------------------------------------------------
  {
    path: 'MACHINE.widthM', group: G.align, label: 'Machine width', kind: 'number',
    min: 0.01, max: 50, step: 0.005, unit: 'm',
    help: 'Equal to target width = 1:1 overlay. Raising this is the lever for a more dramatic hero can.',
  },
  { path: 'MACHINE.alignMode', group: G.align, label: 'Vertical align', kind: 'enum', options: ['center', 'bottom'] },
  { path: 'MACHINE.alignOffset.x', group: G.align, label: 'Offset X', kind: 'number', min: -5, max: 5, step: 0.005 },
  { path: 'MACHINE.alignOffset.y', group: G.align, label: 'Offset Y', kind: 'number', min: -5, max: 5, step: 0.005 },
  { path: 'MACHINE.alignOffset.z', group: G.align, label: 'Offset Z', kind: 'number', min: -5, max: 5, step: 0.005 },
  { path: 'MACHINE.alignRotationDeg.x', group: G.align, label: 'Rotate X', kind: 'number', min: -180, max: 180, step: 0.5, unit: '°' },
  { path: 'MACHINE.alignRotationDeg.y', group: G.align, label: 'Rotate Y', kind: 'number', min: -180, max: 180, step: 0.5, unit: '°' },
  { path: 'MACHINE.alignRotationDeg.z', group: G.align, label: 'Rotate Z', kind: 'number', min: -180, max: 180, step: 0.5, unit: '°' },
  { path: 'MACHINE.showSideShelves', group: G.align, label: 'Show side shelves', kind: 'boolean' },

  // --- Hero can -------------------------------------------------------------
  {
    path: 'HERO_CAN.distanceM', group: G.hero, label: 'Settle distance', kind: 'number',
    min: 0.04, max: 2, step: 0.005, unit: 'm',
    help: 'In front of the CAMERA. Converted with worldUnitsPerMeter, never the anchor factor.',
  },
  {
    path: 'HERO_CAN.minMarginM', group: G.hero, label: 'Min margin', kind: 'number',
    min: 0.005, max: 1, step: 0.005, unit: 'm',
    help: 'Keeps the can in front of the target plane when the user leans in close.',
  },
  { path: 'HERO_CAN.standingThreshold', group: G.hero, label: 'Standing threshold', kind: 'number', min: 0.01, max: 0.99, step: 0.01 },

  // --- Tracking -------------------------------------------------------------
  {
    path: 'POSE.minCutoffHz', group: G.tracking, label: 'Smoothing (still)', kind: 'number',
    min: 0.1, max: 30, step: 0.1, unit: 'Hz',
    help: 'Lower = steadier when the phone is held still. Raise if the model feels sluggish to settle.',
  },
  {
    path: 'POSE.betaPerMps', group: G.tracking, label: 'Release with motion', kind: 'number',
    min: 0, max: 200, step: 1,
    help: 'Higher = tracks tighter while moving. Raise this first if the model drifts off during a pan.',
  },
  {
    path: 'POSE.maxDriftM', group: G.tracking, label: 'Max drift', kind: 'number',
    min: 0, max: 0.05, step: 0.0005, unit: 'm',
    help: 'Hard ceiling on how far the model may trail the target. 0.004 = 4 mm. 0 disables.',
  },
  {
    path: 'POSE.maxDriftDeg', group: G.tracking, label: 'Max drift (angle)', kind: 'number',
    min: 0, max: 30, step: 0.1, unit: '°',
  },
  { path: 'POSE.speedSmoothingS', group: G.tracking, label: 'Speed estimate', kind: 'number', min: 0, max: 0.5, step: 0.005, unit: 's' },
  { path: 'POSE.snapOnReacquire', group: G.tracking, label: 'Snap on re-acquire', kind: 'boolean', help: 'Off = the model slides in from where it was last seen.' },
  {
    path: 'POSE.predictionMs', group: G.tracking, label: 'Forward prediction', kind: 'int',
    min: 0, max: 200, step: 5, unit: 'ms',
    help: 'Draws the model where the target will be rather than where the tracker last saw it — the only lever against MindAR\'s own latency. 0 = off. Start near the HUD\'s typical pose age; too high overshoots when a shake reverses.',
  },
  {
    path: 'POSE.maxPredictionM', group: G.tracking, label: 'Prediction cap', kind: 'number',
    min: 0, max: 0.2, step: 0.001, unit: 'm',
    help: 'Ceiling on how far prediction may move the model. 0 = no ceiling.',
  },
  {
    path: 'POSE.maxPredictionHorizonMs', group: G.tracking, label: 'Prediction horizon cap', kind: 'int',
    min: 0, max: 500, step: 10, unit: 'ms',
    help: 'Ceiling on pose age + prediction, so a pose frozen by a tracking dropout is not extrapolated off the card.',
  },
  {
    path: 'MINDAR.filterMinCF', group: G.tracking, label: 'Filter minCF', kind: 'number',
    min: 0.00001, max: 1, step: 0.0001,
    help: 'One-euro cutoff floor, in cycles per MILLISECOND (MindAR feeds the filter Date.now()), so 0.001 behaves like ~1 Hz. Lower = smoother when still, but also a longer freeze at the start of every movement. Requires a reload.',
  },
  {
    path: 'MINDAR.filterBeta', group: G.tracking, label: 'Filter beta', kind: 'number',
    min: 0, max: 100000, step: 1,
    help: 'Speed term. Deliberately near MindAR\'s stock 1000: smoothing belongs to POSE above, which is frame-rate independent and drift-clamped. Below ~100 it smooths the rotation elements only, which reads as the model sliding. Requires a reload.',
  },
  {
    path: 'MINDAR.cameraVerticalFovDeg', group: G.tracking, label: 'Camera vertical FOV', kind: 'number',
    min: 20, max: 120, step: 0.1, unit: '°',
    help: 'MindAR hardcodes 45°, which biases the pose of anything standing OFF the card — the machine leans as you move sideways. 45 = no override. CALIBRATE it (debug panel → fov calib), never guess. Requires a reload.',
  },
  { path: 'MINDAR.warmupTolerance', group: G.tracking, label: 'Warmup tolerance', kind: 'int', min: 0, max: 60, step: 1, unit: 'frames' },
  { path: 'MINDAR.missTolerance', group: G.tracking, label: 'Miss tolerance', kind: 'int', min: 0, max: 120, step: 1, unit: 'frames', help: 'Raise to ride out brief occlusions.' },

  // --- Rendering ------------------------------------------------------------
  { path: 'RENDER.pixelRatioCap', group: G.render, label: 'Pixel ratio cap', kind: 'number', min: 0.5, max: 4, step: 0.25, help: 'MindAR sets devicePixelRatio uncapped and forces antialias on. This is the main perf lever.' },
  { path: 'RENDER.envPmremSigma', group: G.render, label: 'Env blur', kind: 'number', min: 0, max: 1, step: 0.005 },
  { path: 'RENDER.dirLight.intensity', group: G.render, label: 'Directional light', kind: 'number', min: 0, max: 10, step: 0.05 },
  { path: 'RENDER.dirLight.color', group: G.render, label: 'Directional colour', kind: 'color' },
  { path: 'RENDER.ambientLight.intensity', group: G.render, label: 'Ambient light', kind: 'number', min: 0, max: 10, step: 0.05 },
  { path: 'RENDER.ambientLight.color', group: G.render, label: 'Ambient colour', kind: 'color' },
  { path: 'RENDER.toneMapping', group: G.render, label: 'Tone mapping', kind: 'enum', options: ['none', 'aces'], help: "The asset was authored for 'none'." },
  { path: 'RENDER.toneExposure', group: G.render, label: 'Tone exposure', kind: 'number', min: 0.1, max: 4, step: 0.05 },
  { path: 'RENDER.emissiveIntensity', group: G.render, label: 'Screen emissive', kind: 'number', min: 0, max: 5, step: 0.05 },

  // --- Interaction ----------------------------------------------------------
  { path: 'INTERACTION.hitboxScale', group: G.interaction, label: 'Hitbox scale', kind: 'number', min: 0.5, max: 6, step: 0.1 },
  { path: 'INTERACTION.hitboxOcclusionCheck', group: G.interaction, label: 'Require nearest hit', kind: 'boolean', help: 'Off = the coin is tappable through the cabinet body.' },
  { path: 'INTERACTION.tapMoveTolerancePx', group: G.interaction, label: 'Tap tolerance', kind: 'int', min: 0, max: 64, step: 1, unit: 'px' },

  // --- Timing ---------------------------------------------------------------
  { path: 'TIMING.coinHintDelayMs', group: G.timing, label: 'Coin hint delay', kind: 'int', min: 0, max: 20000, step: 50, unit: 'ms' },
  { path: 'TIMING.screenFadeMs', group: G.timing, label: 'Screen fade', kind: 'int', min: 0, max: 3000, step: 10, unit: 'ms' },
  { path: 'TIMING.prizeLatencyMs', group: G.timing, label: 'Mock draw latency', kind: 'range2', min: 0, max: 10000, step: 10, unit: 'ms' },
  { path: 'TIMING.prizeTimeoutMs', group: G.timing, label: 'Draw timeout', kind: 'int', min: 500, max: 60000, step: 100, unit: 'ms' },
  { path: 'TIMING.idleResumeDelayMs', group: G.timing, label: 'Idle resume delay', kind: 'int', min: 0, max: 10000, step: 50, unit: 'ms' },
  { path: 'TIMING.idleTimeoutMs', group: G.timing, label: 'Idle timeout', kind: 'int', min: 5000, max: 3600000, step: 1000, unit: 'ms', nullable: true, help: 'Empty = disabled.' },
  { path: 'TIMING.maxFrameDtS', group: G.timing, label: 'Max frame delta', kind: 'number', min: 0.008, max: 1, step: 0.001, unit: 's', help: 'Mixer clamp. Without it a backgrounded tab fast-forwards the reveal.' },

  // --- Animation ------------------------------------------------------------
  { path: 'ANIMATION.idleStart', group: G.animation, label: 'Idle starts', kind: 'enum', options: ['onTargetFound', 'onJoinGate'] },
  { path: 'POSE.revealMs', group: G.animation, label: 'Reveal duration', kind: 'int', min: 0, max: 3000, step: 20, unit: 'ms', help: '0 = pop in instantly.' },
  { path: 'POSE.revealFromScale', group: G.animation, label: 'Reveal from scale', kind: 'number', min: 0.1, max: 1, step: 0.01 },

  // --- Prize ----------------------------------------------------------------
  { path: 'PRIZE.mode', group: G.prize, label: 'Prize mode', kind: 'enum', options: ['fixed', 'random'] },
  { path: 'PRIZE.fixedId', group: G.prize, label: 'Fixed figure', kind: 'enum', options: ['A', 'B', 'C'] },

  // --- Coupon save ----------------------------------------------------------
  {
    path: 'COUPON.iosMode', group: G.coupon, label: 'iOS save route', kind: 'enum', options: ['layer', 'share'],
    help: "'layer' shows the image to long-press into Photos. 'share' opens the OS share sheet. Android always downloads.",
  },
  {
    path: 'COUPON.fileName', group: G.coupon, label: 'File name', kind: 'text',
    help: '`{code}` is replaced with the coupon code. Keep it ASCII — some Android download managers mangle Hangul.',
  },
  {
    path: 'COUPON.savedToastMs', group: G.coupon, label: 'Saved toast', kind: 'int',
    min: 500, max: 10000, step: 100, unit: 'ms', help: 'Android only; the download itself is silent.',
  },

  // --- Assets ---------------------------------------------------------------
  { path: 'ASSET.coupon', group: G.asset, label: 'Coupon image', kind: 'text' },
  { path: 'ASSET.qr', group: G.asset, label: 'QR image', kind: 'text' },
  {
    path: 'APP_URL', group: G.asset, label: 'App URL', kind: 'text', readOnly: true,
    help: 'Build-time only — the QR PNG is generated from this. Change it and re-run `npm run asset:qr`.',
  },

  // --- Debug ----------------------------------------------------------------
  { path: 'DEBUG.tapCount', group: G.debug, label: 'Logo taps', kind: 'int', min: 1, max: 20, step: 1 },
  { path: 'DEBUG.requireLongPress', group: G.debug, label: 'Require long press', kind: 'boolean' },
  { path: 'DEBUG.longPressMs', group: G.debug, label: 'Long press', kind: 'int', min: 100, max: 5000, step: 50, unit: 'ms' },
  {
    path: 'DEBUG.recorder.durationS', group: G.debug, label: 'Recording length', kind: 'int',
    min: 3, max: 120, step: 1, unit: 's', help: 'Applies to the next recording; no reload needed.',
  },
  {
    path: 'DEBUG.recorder.seriesHz', group: G.debug, label: 'Trace rate', kind: 'number',
    min: 0, max: 30, step: 1, unit: 'Hz', help: 'Rows per second in the report\'s series block. 0 omits it.',
  },
  {
    path: 'DEBUG.recorder.jumpDeg', group: G.debug, label: 'Jump threshold', kind: 'number',
    min: 0.1, max: 45, step: 0.1, unit: '°',
    help: 'A single pose update turning by more than this counts as a tracker jump rather than movement.',
  },
  { path: 'DEBUG.recorder.jumpMm', group: G.debug, label: 'Jump threshold (mm)', kind: 'number', min: 0.1, max: 100, step: 0.1, unit: 'mm' },
  {
    path: 'DEBUG.recorder.restSpeedMps', group: G.debug, label: 'Rest threshold', kind: 'number',
    min: 0, max: 1, step: 0.005, unit: 'm/s', help: 'Below this the phone counts as held still, which is where jitter is measured.',
  },
  { path: 'DEBUG.recorder.maxEvents', group: G.debug, label: 'Max events listed', kind: 'int', min: 1, max: 200, step: 1 },
];

/** Walk COPY and emit one text field per leaf, so every string is editable without listing 60 paths. */
function copyFields(): Field[] {
  const out: Field[] = [];
  const walk = (node: unknown, prefix: string): void => {
    if (typeof node === 'string') {
      out.push({
        path: prefix,
        group: G.copy,
        label: prefix.replace(/^COPY\./, ''),
        kind: node.includes('\n') ? 'multiline' : 'text',
      });
      return;
    }
    if (node && typeof node === 'object') {
      for (const [k, v] of Object.entries(node)) walk(v, `${prefix}.${k}`);
    }
  };
  walk(DEFAULTS.COPY, 'COPY');
  return out;
}

export const FIELDS: readonly Field[] = [...CORE_FIELDS, ...copyFields()];

export const FIELD_BY_PATH: ReadonlyMap<string, Field> = new Map(FIELDS.map((f) => [f.path, f]));

export const GROUPS: readonly string[] = [...new Set(FIELDS.map((f) => f.group))];
